// WAAP "API Protection → Data Loss Prevention" — managed PII rules (read +
// whole-object update) and the whitelist / selected-API option lists.
// Account-level (any of the account's domain ids resolves the customer).
//   GET        /api-protection/data-loss/rules
//   GET/PUT    /api-protection/data-loss/rules/:ruleId
//   GET        /api-protection/data-loss/api-asset-options
//   GET        /api-protection/data-loss/api-app-options
import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';

export interface WaapDlpWhitelistEntry {
  key: string; // app | api
  operation: string; // ==
  value: string[];
}

export interface WaapDlpRuleConfig {
  description: string;
  pii_type: string;
  class: string;
  pii_level: string; // high | medium | low
  pii_char: string;
  match_content: string;
  enabled: boolean;
  req_enabled: boolean; // Detail "Request" target
  is_pii_masked: boolean; // Data Masking "Enable"
  is_pii_resp_block: boolean;
  mask_all_api: boolean; // true = All APIs, false = Selected APIs
  mask_specified_api: string[];
  pre_n: number; // First X chars masked
  last_m: number; // Last Y chars masked
  whitelist: WaapDlpWhitelistEntry[];
}

/** A managed data-loss (PII) rule. type 1 = managed (fixed catalog). */
export interface WaapDlpRule {
  id: number;
  name: string;
  type: number;
  config: WaapDlpRuleConfig;
  created?: number;
  updated?: number;
  status: number; // 1 = on, 0 = off (table toggle)
  customer_id?: number;
}

/** A {label,value} option for the whitelist / selected-API pickers. */
export interface WaapDlpOption {
  label: string;
  value: string;
}

const rulesBase = (domainId: string) => waapPath(domainId, WAAP_AREAS.apiProtectionDataLossRules);
const rulesKey = (domainId: string) => waapKeys.aspect(domainId, WAAP_AREAS.apiProtectionDataLossRules);

/** List all managed data-loss rules. */
export function useWaapDataLossRules(domainId: string | undefined) {
  return useQuery({
    queryKey: rulesKey(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapDlpRule[]> => {
      const res = await serviceApiV3.get<{ rules: WaapDlpRule[] }>(rulesBase(domainId as string));
      return res.data?.rules ?? [];
    },
  });
}

/** Read one managed data-loss rule (editor prefill). */
export function useWaapDataLossRule(domainId: string | undefined, ruleId: string | undefined) {
  return useQuery({
    queryKey: [...rulesKey(domainId ?? ''), ruleId],
    enabled: !!domainId && !!ruleId,
    retry: false,
    queryFn: async (): Promise<WaapDlpRule | null> => {
      const res = await serviceApiV3.get<WaapDlpRule>(`${rulesBase(domainId as string)}/${ruleId}`);
      return res.data ?? null;
    },
  });
}

/** Edit a managed data-loss rule (status / whitelist / masking). */
export function useUpdateWaapDataLossRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { id: number; body: WaapDlpRule }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: WaapDlpRule }) =>
      (await serviceApiV3.put(`${rulesBase(domainId)}/${id}`, body)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: rulesKey(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Toggle a rule's status in place — optimistic, NO refetch, so the row keeps its
 *  position (the list endpoint re-sorts the just-updated rule to the top, which
 *  causes a jarring reorder + full re-render on every toggle). */
export function useToggleWaapDataLossRule(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: WaapDlpRule }) =>
      (await serviceApiV3.put(`${rulesBase(domainId)}/${id}`, body)).data,
    onMutate: async ({ id, body }: { id: number; body: WaapDlpRule }) => {
      await qc.cancelQueries({ queryKey: rulesKey(domainId) });
      const prev = qc.getQueryData<WaapDlpRule[]>(rulesKey(domainId));
      qc.setQueryData<WaapDlpRule[]>(rulesKey(domainId), (old) =>
        (old ?? []).map((r) => (r.id === id ? { ...r, status: body.status } : r)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(rulesKey(domainId), ctx.prev);
    },
  });
}

function useDlpOptions(domainId: string | undefined, area: (typeof WAAP_AREAS)[keyof typeof WAAP_AREAS]) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', area),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapDlpOption[]> => {
      const res = await serviceApiV3.get<{ options: WaapDlpOption[] }>(waapPath(domainId as string, area));
      return res.data?.options ?? [];
    },
  });
}

/** API-asset options (Selected APIs / whitelist by API). */
export function useWaapDataLossApiAssetOptions(domainId: string | undefined) {
  return useDlpOptions(domainId, WAAP_AREAS.apiProtectionDataLossApiAssetOptions);
}

/** Application options (whitelist by application). */
export function useWaapDataLossApiAppOptions(domainId: string | undefined) {
  return useDlpOptions(domainId, WAAP_AREAS.apiProtectionDataLossApiAppOptions);
}
